/**
 * Academic Records Field Transformer
 * Handles field name mapping between frontend and backend for academic records
 * and manages field clearing logic when Degree_Type__c changes
 */

import moment from 'moment';

// Field mapping configuration for academic records
const ACADEMIC_FIELD_MAPPING = {
  // Frontend field name -> Backend field name
  frontend_to_backend: {
    'degree_type_frontend': 'Degree_Type__c',
    'degree_level_others_frontend': 'Degree_Level_Others__c',
    'degree_name_frontend': 'Degree_Name__c',
    'degree_name_other_frontend': 'Degree_Name_Other__c',
    'board_frontend': 'Board__c',
    'board_others_frontend': 'Board_Others__c',
    'university_name_frontend': 'University_Name__c',
    'university_others_frontend': 'University_Others__c',
    'specialization_frontend': 'Specialization__c',
    'year_completion_frontend': 'Year_of_Completion__c',
    'evaluation_type_frontend': 'Evaluation_type__c',
    'max_cgpa_frontend': 'Maximum_CGPA_GPA_Grade_Percentage_Points__c',
    'obtained_cgpa_frontend': 'Obtained_CGPA_GPA_Grade_Percentage_Point__c',
    'equivalent_percentage_frontend': 'Equivalent_Percentage__c',
  },
  
  // Backend field name -> Frontend field name
  backend_to_frontend: {
    'Degree_Type__c': 'degree_type_frontend',
    'Degree_Level_Others__c': 'degree_level_others_frontend',
    'Degree_Name__c': 'degree_name_frontend',
    'Degree_Name_Other__c': 'degree_name_other_frontend',
    'Board__c': 'board_frontend',
    'Board_Others__c': 'board_others_frontend',
    'University_Name__c': 'university_name_frontend',
    'University_Others__c': 'university_others_frontend',
    'Specialization__c': 'specialization_frontend',
    'Year_of_Completion__c': 'year_completion_frontend',
    'Evaluation_type__c': 'evaluation_type_frontend',
    'Maximum_CGPA_GPA_Grade_Percentage_Points__c': 'max_cgpa_frontend',
    'Obtained_CGPA_GPA_Grade_Percentage_Point__c': 'obtained_cgpa_frontend',
    'Equivalent_Percentage__c': 'equivalent_percentage_frontend',
  }
};

// Fields that should be cleared when Degree_Type__c changes
const DEGREE_TYPE_DEPENDENT_FIELDS = {
  frontend: [
    'degree_level_others_frontend',
    'degree_name_frontend',
    'degree_name_other_frontend',
    'board_frontend',
    'board_others_frontend',
    'university_name_frontend',
    'university_others_frontend',
    'specialization_frontend',
    'year_completion_frontend',
    'evaluation_type_frontend',
    'max_cgpa_frontend',
    'obtained_cgpa_frontend',
    'equivalent_percentage_frontend'
  ],
  backend: [
    'Degree_Level_Others__c',
    'Degree_Name__c',
    'Board__c',
    'Board_Others__c',
    'University_Name__c',
    'University_Others__c',
    'Specialization__c',
    'Year_of_Completion__c',
    'Evaluation_type__c',
    'Maximum_CGPA_GPA_Grade_Percentage_Points__c',
    'Obtained_CGPA_GPA_Grade_Percentage_Point__c',
    'Equivalent_Percentage__c'
  ]
};

/**
 * Transform academic records from frontend format to backend format for API submission
 * @param {Array} academicRecords - Array of academic record objects with frontend field names
 * @returns {Array} - Array of academic record objects with backend field names
 */
export const transformAcademicRecordsForAPI = (academicRecords) => {
  if (!Array.isArray(academicRecords)) {
    console.warn('transformAcademicRecordsForAPI: Input is not an array');
    return [];
  }

  console.log('transformAcademicRecordsForAPI - Input:', academicRecords);

  const transformedRecords = academicRecords.map((record, index) => {
    if (!record || typeof record !== 'object') {
      console.warn(`transformAcademicRecordsForAPI: Record at index ${index} is not a valid object`);
      return record;
    }

    const transformedRecord = { ...record };

    // Transform frontend field names to backend field names
    Object.keys(ACADEMIC_FIELD_MAPPING.frontend_to_backend).forEach(frontendField => {
      const backendField = ACADEMIC_FIELD_MAPPING.frontend_to_backend[frontendField];
      
      if (transformedRecord.hasOwnProperty(frontendField)) {
        // Copy value to backend field name
        transformedRecord[backendField] = transformedRecord[frontendField];
        // Remove frontend field name
        delete transformedRecord[frontendField];
        
        console.log(`transformAcademicRecordsForAPI - Mapped ${frontendField} -> ${backendField}:`, transformedRecord[backendField]);
      }
    });

    // Format Year_of_Completion__c to DD-MMM-YYYY format (e.g., 10-JAN-2005)
    // This handles both cases: when it comes from frontend mapping or when it already exists
    if (transformedRecord['Year_of_Completion__c']) {
      const yearValue = transformedRecord['Year_of_Completion__c'];
      // Check if it's a valid date
      if (yearValue && moment(yearValue, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY', 'DD-MMM-YYYY', moment.ISO_8601], true).isValid()) {
        // Format to DD-MMM-YYYY with uppercase month (e.g., 10-JAN-2005)
        const formattedDate = moment(yearValue).format('DD-MMM-YYYY');
        // Ensure month abbreviation is uppercase
        const parts = formattedDate.split('-');
        if (parts.length === 3) {
          transformedRecord['Year_of_Completion__c'] = `${parts[0]}-${parts[1].toUpperCase()}-${parts[2]}`;
        } else {
          transformedRecord['Year_of_Completion__c'] = formattedDate;
        }
        console.log(`transformAcademicRecordsForAPI - Formatted Year_of_Completion__c from "${yearValue}" to:`, transformedRecord['Year_of_Completion__c']);
      }
    }

    // Apply field cleaning logic based on evaluation type
    if (transformedRecord['Evaluation_type__c'] === 'Percentage') {
      console.log('transformAcademicRecordsForAPI - Clearing CGPA fields for Percentage evaluation type');
      transformedRecord['Maximum_CGPA_GPA_Grade_Percentage_Points__c'] = null;
      transformedRecord['Obtained_CGPA_GPA_Grade_Percentage_Point__c'] = null;
    }
    
    if (transformedRecord['Evaluation_type__c'] === 'CGPA') {
      console.log('transformAcademicRecordsForAPI - Clearing Percentage fields for CGPA evaluation type');
      transformedRecord['Equivalent_Percentage__c'] = null;
    }

    // Clear dependent fields based on other field values
    if (transformedRecord['Degree_Type__c'] !== 'Others') {
      transformedRecord['Degree_Level_Others__c'] = ' ';
    }
    
    if (transformedRecord['Board__c'] !== 'Others') {
      transformedRecord['Board_Others__c'] = ' ';
    }
    
    if (transformedRecord['University_Name__c'] !== 'Others') {
      transformedRecord['University_Others__c'] = ' ';
    }

    console.log(`transformAcademicRecordsForAPI - Transformed record ${index}:`, transformedRecord);
    return transformedRecord;
  });

  console.log('transformAcademicRecordsForAPI - Output:', transformedRecords);
  return transformedRecords;
};

/**
 * Transform academic records from backend format to frontend format for form display
 * @param {Array} academicRecords - Array of academic record objects with backend field names
 * @returns {Array} - Array of academic record objects with frontend field names
 */
export const transformAcademicRecordsFromAPI = (academicRecords) => {
  if (!Array.isArray(academicRecords)) {
    console.warn('transformAcademicRecordsFromAPI: Input is not an array');
    return [];
  }

  console.log('transformAcademicRecordsFromAPI - Input:', academicRecords);

  const transformedRecords = academicRecords.map((record, index) => {
    if (!record || typeof record !== 'object') {
      console.warn(`transformAcademicRecordsFromAPI: Record at index ${index} is not a valid object`);
      return record;
    }

    const transformedRecord = { ...record };

    // Transform backend field names to frontend field names
    Object.keys(ACADEMIC_FIELD_MAPPING.backend_to_frontend).forEach(backendField => {
      const frontendField = ACADEMIC_FIELD_MAPPING.backend_to_frontend[backendField];
      
      if (transformedRecord.hasOwnProperty(backendField)) {
        // Copy value to frontend field name
        transformedRecord[frontendField] = transformedRecord[backendField];
        // Keep backend field name as well for compatibility
        // Don't delete the backend field in case it's needed elsewhere
        
        console.log(`transformAcademicRecordsFromAPI - Mapped ${backendField} -> ${frontendField}:`, transformedRecord[frontendField]);
      }
    });

    console.log(`transformAcademicRecordsFromAPI - Transformed record ${index}:`, transformedRecord);
    return transformedRecord;
  });

  console.log('transformAcademicRecordsFromAPI - Output:', transformedRecords);
  return transformedRecords;
};

/**
 * Clear dependent fields when Degree_Type__c changes
 * @param {Object} academicRecord - Single academic record object
 * @param {string} newDegreeType - New degree type value
 * @param {boolean} useFrontendFields - Whether to use frontend field names
 * @returns {Object} - Academic record with cleared dependent fields
 */
export const clearDependentFieldsOnDegreeTypeChange = (academicRecord, newDegreeType, useFrontendFields = true) => {
  if (!academicRecord || typeof academicRecord !== 'object') {
    console.warn('clearDependentFieldsOnDegreeTypeChange: Invalid academic record provided');
    return academicRecord;
  }

  console.log('clearDependentFieldsOnDegreeTypeChange - Input:', {
    academicRecord,
    newDegreeType,
    useFrontendFields
  });

  const clearedRecord = { ...academicRecord };
  const fieldsToKeep = useFrontendFields ? 
    ['degree_type_frontend'] : 
    ['Degree_Type__c'];
  
  const dependentFields = useFrontendFields ? 
    DEGREE_TYPE_DEPENDENT_FIELDS.frontend : 
    DEGREE_TYPE_DEPENDENT_FIELDS.backend;

  // Set the new degree type
  const degreeTypeField = useFrontendFields ? 'degree_type_frontend' : 'Degree_Type__c';
  clearedRecord[degreeTypeField] = newDegreeType;

  // Clear all dependent fields except those we want to keep
  dependentFields.forEach(field => {
    if (!fieldsToKeep.includes(field)) {
      if (typeof clearedRecord[field] === 'string') {
        clearedRecord[field] = '';
      } else {
        clearedRecord[field] = null;
      }
      console.log(`clearDependentFieldsOnDegreeTypeChange - Cleared field: ${field}`);
    }
  });

  console.log('clearDependentFieldsOnDegreeTypeChange - Output:', clearedRecord);
  return clearedRecord;
};

/**
 * Generate modified payload with transformed academic records for API submission
 * @param {Object} payload - Original payload object
 * @returns {Object} - Modified payload with transformed academic records
 */
export const generateModifiedPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    console.warn('generateModifiedPayload: Invalid payload provided');
    return payload;
  }

  console.log('generateModifiedPayload - Input payload:', payload);

  const modifiedPayload = { ...payload };

  // Transform academicRecords if present
  if (modifiedPayload.academicRecords && Array.isArray(modifiedPayload.academicRecords)) {
    modifiedPayload.academicRecords = transformAcademicRecordsForAPI(modifiedPayload.academicRecords);
    console.log('generateModifiedPayload - Transformed academicRecords');
  }

  // Also transform educationHistories if present (for compatibility)
  if (modifiedPayload.educationHistories && Array.isArray(modifiedPayload.educationHistories)) {
    modifiedPayload.educationHistories = transformAcademicRecordsForAPI(modifiedPayload.educationHistories);
    console.log('generateModifiedPayload - Transformed educationHistories');
  }

  console.log('generateModifiedPayload - Output payload:', modifiedPayload);
  return modifiedPayload;
};

/**
 * Handle Degree_Type__c field change in form
 * @param {Function} setValue - React Hook Form setValue function
 * @param {Function} getValues - React Hook Form getValues function
 * @param {number} recordIndex - Index of the academic record being modified
 * @param {string} newDegreeType - New degree type value
 * @param {boolean} useFrontendFields - Whether to use frontend field names
 */
export const handleDegreeTypeChange = (setValue, getValues, recordIndex, newDegreeType, useFrontendFields = true) => {
  console.log('handleDegreeTypeChange - Called with:', {
    recordIndex,
    newDegreeType,
    useFrontendFields
  });

  const currentValues = getValues();
  const academicRecords = currentValues.academicRecords || [];
  
  if (recordIndex >= academicRecords.length) {
    console.warn(`handleDegreeTypeChange: Record index ${recordIndex} is out of bounds`);
    return;
  }

  const currentRecord = academicRecords[recordIndex];
  const clearedRecord = clearDependentFieldsOnDegreeTypeChange(currentRecord, newDegreeType, useFrontendFields);

  // Update the specific record in the form
  const updatedRecords = [...academicRecords];
  updatedRecords[recordIndex] = clearedRecord;
  
  setValue('academicRecords', updatedRecords, { 
    shouldDirty: true, 
    shouldTouch: true,
    shouldValidate: true 
  });

  console.log('handleDegreeTypeChange - Updated academic records:', updatedRecords);
};

export default {
  transformAcademicRecordsForAPI,
  transformAcademicRecordsFromAPI,
  clearDependentFieldsOnDegreeTypeChange,
  generateModifiedPayload,
  handleDegreeTypeChange,
  ACADEMIC_FIELD_MAPPING,
  DEGREE_TYPE_DEPENDENT_FIELDS
};